// Forked into ../redesign/Results/GroupCollections.tsx — keep in sync until phase_0 cleanup.
import React, { useContext } from 'react';
import { useTranslate } from '@bynder/localization';
import { IconInbox } from '@bynder/icons';
import type { CollectionsSearchResults } from '../types';
import { iconSizeExtraLarge } from '../../../helpers/iconSizes';
import { InfoContext } from '../../../context';
import { addDomainToUrlIfNeeded } from '../../../helpers/url';
import {
	ClassicItem,
	ClassicNavigableHeadingButton,
	StyledImgWrapper,
	StyledInfoDiv,
	StyledItemsUL,
	StyledResultsSection,
	StyledViewAll,
} from './StyledComponents';
import { navigateTo } from '../../../helpers/windowLocation';
import { getSpotlightResultClassName } from '../helpers/getSpotlightResultClassName';
import { COLLECTION_NAME, parseHighlightedText } from '../helpers/highlightedText';
import { HighlightedText } from '../redesign/HighlightedText';
import { useSearch } from '../SpotlightState';

interface Props {
	collectionResults: CollectionsSearchResults;
}

export function GroupCollections({ collectionResults }: Props) {
	const { searchTerm } = useSearch();
	const { translate } = useTranslate();
	const {
		general: { portalDomain },
	} = useContext(InfoContext);

	const goToCollectionsSearchPage = () => {
		navigateTo(`/collections/?box=all&searchTerm=${encodeURIComponent(searchTerm)}`);
	};

	return (
		<StyledResultsSection>
			<ClassicNavigableHeadingButton onSelect={goToCollectionsSearchPage}>
				<div>{translate('spotlight.dropdown.collections')}</div>
				<StyledViewAll>
					<span>{translate('spotlight.dropdown.viewAll')}</span>
					<span> ({collectionResults.total})</span>
				</StyledViewAll>
			</ClassicNavigableHeadingButton>

			<StyledItemsUL>
				{collectionResults.items.map((collection) => {
					const icon = collection.thumbnailUrl ? (
						<img
							src={addDomainToUrlIfNeeded(collection.thumbnailUrl, portalDomain)}
							alt=""
						/>
					) : (
						<IconInbox size={iconSizeExtraLarge} />
					);

					const linkToCollection = collection.path
						? collection.path
						: `/collections/view/${collection.id}/`;

					const count =
						collection.mediaCount === undefined
							? collection.assetCount
							: collection.mediaCount;

					return (
						<ClassicItem
							dataId={collection.id}
							onSelect={() => navigateTo(linkToCollection)}
							className={getSpotlightResultClassName('Collection')}
							key={collection.id}
						>
							<StyledImgWrapper>{icon}</StyledImgWrapper>
							<StyledInfoDiv>
								<div>
									<HighlightedText
										segments={parseHighlightedText(
											collection.name,
											COLLECTION_NAME,
										)}
									/>
								</div>
								<div>
									{`${count} ${translate('spotlight.dropdown.collectionFiles')}`}
								</div>
							</StyledInfoDiv>
						</ClassicItem>
					);
				})}
			</StyledItemsUL>
		</StyledResultsSection>
	);
}
